import { useState } from 'react'
import { useTasks } from '../context/TaskContext'
import TaskModal from './TaskModal'
import { motion, AnimatePresence } from 'framer-motion'
import { Menu, Plus, Calendar, CheckCircle2, Clock } from 'lucide-react'

function Header({ onMenuClick }) {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { todayTasks, stats } = useTasks()

  const completedToday = todayTasks.filter(t => t.status === 'completed').length
  const totalToday = todayTasks.length
  const progress = totalToday > 0 ? (completedToday / totalToday) * 100 : 0

  const currentDate = new Date().toLocaleDateString('en-US', { 
    weekday: 'long', 
    month: 'long', 
    day: 'numeric' 
  })

  return (
    <>
      <motion.header 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-6 border-b border-gray-200/50 dark:border-gray-700/50"
      >
        <div className="flex items-center gap-3">
          {/* Mobile Menu Button */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onMenuClick}
            className="lg:hidden p-2.5 rounded-xl bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </motion.button>
          
          <div>
            <motion.h2 
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white"
            >
              {currentDate}
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-2"
            >
              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
              <span>{completedToday} of {totalToday} tasks completed today</span>
            </motion.p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          {/* Progress bar - Desktop */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="hidden md:block w-48"
          >
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" />
                Daily Progress
              </span>
              <span className="font-semibold text-gray-900 dark:text-white">{Math.round(progress)}%</span>
            </div>
            <div className="h-2.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
                className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-purple-600"
              />
            </div>
          </motion.div>

          {/* Add task button */}
          <motion.button
            whileHover={{ scale: 1.02, boxShadow: "0 10px 30px rgba(99, 102, 241, 0.4)" }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setIsModalOpen(true)}
            className="btn btn-primary flex items-center gap-2 px-5 py-3 rounded-xl"
          >
            <Plus className="w-5 h-5" />
            <span className="hidden sm:inline">New Task</span>
            <span className="sm:hidden">Add</span>
          </motion.button>
        </div>
      </motion.header>

      <AnimatePresence>
        {isModalOpen && (
          <TaskModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
          />
        )}
      </AnimatePresence>
    </>
  )
}

export default Header
